// use this axiosInstance as a function to make api calls;
import axiosInstance from '../axios';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { getTokenFromLocalStorage } from '../../helpers';
import { COMMENT, QUALIFIERS } from '../../enums';
import { getVisibleFeedComments } from '../../helpers/comments';

export const fetchVideoFeedData = async (id: string) => {
  const response = await axiosInstance.get(`${API_ROUTES.VIDEOFEED}/${id}`);
  return { ...response, data: getVisibleFeedComments(response.data) };
};

export const fetchVideoFeedVideosAndType = async (id: string) => {
  if (!id) {
    return { data: {} } as any;
  }

  const response = await axiosInstance.get(`${API_ROUTES.VIDEOFEED_VIDEOS_AND_TYPE}/${id}`);
  return response;
};

export const fetchNotificationVideoFeedData = async (id: string) => {
  const response = await axiosInstance.get(`${API_ROUTES.NOTIFICATION_VIDEOFEED}/${id}`);
  return response;
};

export const downloadJSONData = async (id: string) => {
  const response = await axiosInstance.get(`${API_ROUTES.DOWNLOAD_JSON}/${id}`);
  return response;
};

export const downloadComposerJSONData = async (id: string) => {
  const response = await axiosInstance.get(`${API_ROUTES.REFLEX_COMPOSER_JSON_DOWNLOAD}/${id}`);
  return response;
};

export const getVideofeedDownloadDataByVideoFeedId = async (videoFeedId: string) => {
  const res = await axiosInstance.get(`${API_ROUTES.VIDEOFEED_VIDEO_DOWNLOAD}/${videoFeedId}`);
  return res;
};

export const downloadTimelineAVaultData = async (id: string) => {
  const token = getTokenFromLocalStorage();
  const base = process.env.REACT_APP_API_URL || '';
  const url = new URL(`${API_ROUTES.TIMELINE_DOWNLOAD_AVAULT}/${id}`, base).toString();

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/zip, application/octet-stream, */*',
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Timeline AVault download failed (${res.status})`);
  }

  return await res.blob();
};

export const downloadVideoData = async (id: string, customName?: boolean) => {
  const response = await axiosInstance.get(`${API_ROUTES.DOWNLOAD_VIDEO}/${id}?${customName && 'customName=' + customName}`);
  return response;
};

export const getAppendedDownloadOptions = async (id: string) => {
  return await axiosInstance.get(`${API_ROUTES.APPENDED_DOWNLOAD_OPTIONS}/${id}`);
};

export const downloadAppendedSourceVideos = async (id: string) => {
  return await axiosInstance.post(`${API_ROUTES.APPENDED_DOWNLOAD_SOURCES}/${id}`);
};

export const startAppendedMergedDownload = async (id: string) => {
  return await axiosInstance.post(`${API_ROUTES.APPENDED_DOWNLOAD_MERGED}/${id}`);
};

export const getAppendedMergedDownloadStatus = async (jobId: number) => {
  return await axiosInstance.get(`${API_ROUTES.APPENDED_DOWNLOAD_MERGED}/${jobId}/status`);
};

export const createVideofeedExternalShare = async (
  id: string,
  data: {
    recipientEmails: string[];
    selectedVideoIds: number[];
    includeJson?: boolean;
    selectedJsonVideofeedIds?: number[];
    message?: string;
    ttlSeconds?: number;
  },
) => {
  return await axiosInstance.post(`${API_ROUTES.VIDEOFEED_EXTERNAL_SHARES}/${id}`, data);
};

export const createVideofeedDownloadableLink = async (
  id: string,
  data?: { selectedVideoIds?: number[]; ttlSeconds?: number },
) => {
  return await axiosInstance.post(
    `${API_ROUTES.VIDEOFEED_EXTERNAL_SHARES}/${id}/downloadable-link`,
    data || {},
  );
};

export const getVideofeedExternalShares = async (id: string) => {
  return await axiosInstance.get(`${API_ROUTES.VIDEOFEED_EXTERNAL_SHARES}/${id}`);
};

export const getVideofeedExternalShareEmails = async () => {
  return await axiosInstance.get(`${API_ROUTES.VIDEOFEED_EXTERNAL_SHARES}/emails`);
};

export const downloadVideofeedExternalShareEmailsJson = async (id: string) => {
  const token = getTokenFromLocalStorage();
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const url = `${base}${API_ROUTES.VIDEOFEED_EXTERNAL_SHARES}/${id}/emails/json`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json, */*',
    },
  });

  if (!res.ok) {
    throw new Error((await res.text()) || `External share emails JSON download failed (${res.status})`);
  }

  return await res.blob();
};

export const downloadVideofeedExternalShareEmailsPlist = async (id: string) => {
  const token = getTokenFromLocalStorage();
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const url = `${base}${API_ROUTES.VIDEOFEED_EXTERNAL_SHARES}/${id}/emails/plist`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/xml, text/xml, */*',
    },
  });

  if (!res.ok) {
    throw new Error((await res.text()) || `External share emails plist download failed (${res.status})`);
  }

  return await res.blob();
};

export const postVideofeedViewRequest = async (id: string) => {
  return await axiosInstance.get(`${API_ROUTES.GET_VIEW_VIDEOFEED}/${id}`);
};

export const createClipForTopic = async (
  data: {
    topic_id: number;
    color?: string;
    time_start: number;
    time_end: number;
    comments?: COMMENT[];
    notes?: { id: number; note: string };
    qualifiers?: QUALIFIERS[];
    clipIndex?: number;
  },
  videofeedId: number,
) => {
  const { topic_id, color = '1.0,1.0,1.0', time_start, time_end, comments, notes, qualifiers, clipIndex } = data;
  return await axiosInstance.post(`${API_ROUTES.CREATE_CLIP_FOR_TOPIC}/${videofeedId}`, {
    topic_id,
    color,
    time_start,
    time_end,
    comments,
    notes,
    qualifiers,
    clipIndex,
  });
};

export const editClip = async (data: { color: string; time_start: number; time_end: number }, clipId: number) => {
  const { color = '1.0,1.0,1.0', time_start, time_end } = data;
  return await axiosInstance.patch(`${API_ROUTES.CLIP}/${clipId}`, {
    color,
    time_start,
    time_end,
  });
};

export const setClipAngleLock = (clipId: string, locked_angle_id: number | null) =>
  axiosInstance.patch(`${API_ROUTES.CLIP}/${clipId}`, { locked_angle_id });

export const removeClipForTopic = async (data: { ids: number[] }, videofeedId: number) => {
  const { ids } = data;
  return await axiosInstance.post(`${API_ROUTES.REMOVE_CLIP_FOR_TOPIC}/${videofeedId}`, {
    ids,
  });
};

export const removeTopic = async (data: { id: number | number[] }, videofeedId: number) => {
  const { id } = data;
  return await axiosInstance.post(`${API_ROUTES.REMOVE_TOPIC}/${videofeedId}`, {
    id,
  });
};

export const createEmptyRow = async (name: string, videofeedId: string, position?: string) => {
  return await axiosInstance.post(`${API_ROUTES.VIDEOFEED}/topic`, {
    name,
    videofeedId,
    position,
  });
};
